import { useState, useRef, useCallback } from 'react';

import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { nanoid } from 'nanoid';
import posthog from 'posthog-js';
import { useSearchParams } from 'react-router-dom';

import type { ChatMessage } from '@cast/types';

import { getUser } from '@cast/infrastructure-auth';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { trackOpsPilot, type QuerySource } from './_analytics';
import {
  createA2AStreamProcessState,
  processA2AStream,
} from './processA2AStream';
import { useA2AClient } from './useA2AClient';
import { useChatbotState } from './useChatbotState';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const isA2ANetworkError = (err: unknown): err is TypeError =>
  err instanceof TypeError &&
  /Failed to fetch|NetworkError|Load failed|network/i.test(err.message);

export const useA2AChat = (
  onAgentResponse?: (
    messages: ChatMessage[],
    contextId: string,
    snapshotChatId: string | undefined
  ) => void,
  onGetActiveChatId?: () => string | undefined,
  onUserMessageSent?: (
    messages: ChatMessage[],
    contextId: string,
    snapshotChatId: string | undefined
  ) => void
) => {
  const contextIdRef = useRef(nanoid());
  const latestRequestIdRef = useRef(0);
  const messagesRef = useRef<ChatMessage[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingChatId, setLoadingChatId] = useState<string | undefined>(
    undefined
  );
  const [thinkingText, setThinkingText] = useState<string | undefined>(
    undefined
  );
  const [error, setError] = useState<string | null>(null);
  const [pendingRetry, setPendingRetry] = useState(false);
  const lastFailedTextRef = useRef<string | null>(null);
  const { currentOrganization } = useOrganizations();
  const [searchParams] = useSearchParams();

  const timezone = searchParams.get('cost_report_timezone') || dayjs.tz.guess();

  const { clientRef, abortInFlight, startNewController } =
    useA2AClient(setError);

  const syncMessages = useCallback((msgs: ChatMessage[]) => {
    messagesRef.current = msgs;
    setMessages(msgs);
  }, []);

  const sendMessage = async (
    text: string,
    snapshotChatId?: string,
    source: QuerySource = 'input'
  ): Promise<boolean> => {
    if (!clientRef.current || !text.trim()) return false;

    setPendingRetry(false);
    lastFailedTextRef.current = null;

    const originChatId = snapshotChatId;
    const originContextId = contextIdRef.current;
    startNewController();
    const requestId = ++latestRequestIdRef.current;

    setLoading(true);
    setLoadingChatId(originChatId);
    setThinkingText(undefined);
    useChatbotState.getState().setIsProcessing(true);

    const newMessage: ChatMessage = { id: nanoid(), role: 'user', text };
    const originMessages = [...messagesRef.current, newMessage];
    syncMessages(originMessages);
    onUserMessageSent?.(originMessages, contextIdRef.current, originChatId);

    // messageIndex is 1-based: count user messages in this conversation so far.
    const messageIndex = originMessages.filter((m) => m.role === 'user').length;
    const startedAt = performance.now();

    trackOpsPilot.querySubmitted({
      context_id: originContextId,
      organization_id: currentOrganization?.id,
      source,
      query_length: text.trim().length,
      message_index: messageIndex,
    });

    const appendMessages = (newMessages: ChatMessage | ChatMessage[]) => {
      const messagesToAdd = Array.isArray(newMessages)
        ? newMessages
        : [newMessages];
      const updatedMessages = [...originMessages, ...messagesToAdd];
      const activeChatId = onGetActiveChatId?.();
      onAgentResponse?.(updatedMessages, originContextId, originChatId);
      if (originChatId === activeChatId) {
        syncMessages(updatedMessages);
      }
    };

    const handleSendError = (errorMessage: ChatMessage) => {
      const messagesWithError = [...originMessages, errorMessage];
      lastFailedTextRef.current = text;
      setPendingRetry(true);
      const activeChatId = onGetActiveChatId?.();
      if (originChatId === activeChatId) {
        syncMessages(messagesWithError);
        onAgentResponse?.(messagesWithError, originContextId, originChatId);
      }
    };

    // Count a completion only for the request that is still the latest in
    // flight. A superseded request (user asked again, or started a new chat,
    // before this one resolved) must not emit `Query completed` — that event is
    // the denominator for the CTR and feedback-rate insights, so a stale
    // double-count would silently deflate both. Mirrors the `finally` guard.
    const trackCompleted = () => {
      if (latestRequestIdRef.current !== requestId) return;
      trackOpsPilot.queryCompleted({
        context_id: originContextId,
        organization_id: currentOrganization?.id,
        latency_ms: Math.round(performance.now() - startedAt),
        message_index: messageIndex,
      });
    };

    // Guard UI updates against a request that has been superseded by a newer
    // sendMessage call. The agent's stream keeps running server-side, but its
    // events should no longer drive visible state once the user has moved on.
    const setActiveThinkingText = (next: string | undefined) => {
      if (latestRequestIdRef.current !== requestId) return;
      setThinkingText(next);
    };

    const clearThinking = () => setActiveThinkingText(undefined);
    const streamState = createA2AStreamProcessState();
    const handlers = {
      setThinkingText: setActiveThinkingText,
      appendMessages,
      trackCompleted,
    };

    try {
      const stream = clientRef.current.sendMessageStream({
        message: {
          contextId: originContextId,
          kind: 'message',
          messageId: newMessage.id,
          role: newMessage.role,
          parts: [{ kind: 'text', text: newMessage.text }],
          metadata: {
            org_id: currentOrganization?.id,
            timezone,
            posthog_session_id: posthog.get_session_id() ?? null,
            user_id: getUser()?.id,
            user_email: getUser()?.email,
          },
        },
      });

      try {
        await processA2AStream(stream, handlers, streamState);
      } catch (err) {
        // Mid-stream TCP drop: resubscribe the same task and keep processing.
        if (
          isA2ANetworkError(err) &&
          streamState.taskId &&
          !streamState.finalized &&
          clientRef.current
        ) {
          const resumed = clientRef.current.resubscribeTask({
            id: streamState.taskId,
          });
          await processA2AStream(resumed, handlers, streamState);
        } else {
          throw err;
        }
      }
      return true;
    } catch (err) {
      clearThinking();
      const aborted = err instanceof DOMException && err.name === 'AbortError';
      const isHttpError =
        err instanceof Error && /HTTP error/i.test(err.message);

      // Extract HTTP status code if present (A2A SDK embeds it in the message)
      const httpStatusMatch = isHttpError ? err.message.match(/(\d{3})/) : null;
      const http_status = httpStatusMatch
        ? parseInt(httpStatusMatch[1], 10)
        : undefined;

      // True transport failures: browser-emitted TypeErrors with known messages.
      // Deliberately excludes generic `instanceof Error` so JS runtime bugs and
      // SDK errors are not misclassified as connectivity issues.
      const isNetworkError = isA2ANetworkError(err);

      const error_type = aborted
        ? 'abort'
        : isHttpError
        ? 'http'
        : isNetworkError
        ? 'network'
        : 'unknown';

      trackOpsPilot.queryFailed({
        context_id: originContextId,
        organization_id: currentOrganization?.id,
        latency_ms: Math.round(performance.now() - startedAt),
        aborted,
        error_type,
        error_message: err instanceof Error ? err.message : String(err),
        ...(http_status != null && { http_status }),
      });
      if (aborted) {
        handleSendError({
          id: nanoid(),
          role: 'agent',
          text: 'Request was canceled.',
        });
        return false;
      }
      console.error('Failed to send message, error: ', err);
      // The A2A SDK surfaces the full raw response body in err.message for
      // HTTP errors (e.g. Cloudflare 502 JSON blobs). Detect those and replace
      // with a user-friendly string so raw JSON is never shown in the chat.
      const errorText = isHttpError
        ? 'Something went wrong. Please try again in a moment.'
        : err instanceof Error
        ? err.message
        : 'An error occurred';
      handleSendError({
        id: nanoid(),
        role: 'agent',
        text: `⚠️ ${errorText}`,
      });
      setError(null);
      return false;
    } finally {
      if (latestRequestIdRef.current === requestId) {
        setLoading(false);
        setLoadingChatId(undefined);
        useChatbotState.getState().setIsProcessing(false);
      }
    }
  };

  const clearPendingRetry = useCallback(() => {
    setPendingRetry(false);
    lastFailedTextRef.current = null;
  }, []);

  const resetChat = useCallback(() => {
    abortInFlight();
    contextIdRef.current = nanoid();
    syncMessages([]);
    setError(null);
    setThinkingText(undefined);
    clearPendingRetry();
  }, [abortInFlight, clearPendingRetry, syncMessages]);

  const setContextId = useCallback((contextId: string) => {
    contextIdRef.current = contextId;
  }, []);

  const getContextId = useCallback(() => contextIdRef.current, []);

  const retrySend = () => {
    const text = lastFailedTextRef.current;
    if (!text) return;
    sendMessage(text, onGetActiveChatId?.());
  };

  return {
    messages,
    loading,
    loadingChatId,
    thinkingText,
    sendMessage,
    error,
    resetChat,
    setMessages: syncMessages,
    abortInFlight,
    setContextId,
    getContextId,
    pendingRetry,
    retrySend,
    clearPendingRetry,
  };
};
